import {createContext} from 'react';
import { RasterShape } from '../types';
import { Fleet } from '../components/map/components/FleetMenu';
import { Service } from '../types/service';

// Types start with a capital letter

type SetContextStringFunction = (value: string | ((prev: string) => string)) => void
type SetContextNumberFunction = (value: number | ((prev: number) => number)) => void

type IconType = 'default' | 'star' | 'location' | 'flag' | 'hospital' | 'school' | 'shopping' | 'airport' | 'bus' | 'train' | 'tram' | 'subway' | 'store' | 'restaurant' | 'parking' | 'dot';

type Point = {
    id: string;
    layerId?: number;
    type: 'point';
    name: string;
    description: string;
    colour: string;
    icon: IconType;
    size?: number; // Size in pixels, optional with default of 24
    isLocked: boolean;
    coordinates: [number, number]; // [latitude, longitude]
    hidden?: boolean;
    creationSource?: 'quick-start-hub' | 'transit-osm-stop' | 'osm-key-location';
}

type Line = {
    id: string;
    layerId?: number;
    type: 'line';
    name: string;
    colour: string;
    description: string;
    opacity: string;
    borderColour: string;
    borderWidth: number;
    isLocked: boolean;
    coordinates: [number, number][];
    dashPattern?: string;
    arrowStyle?: string;
    hidden?: boolean;
    time?: string | number | null;
    numPassengers?: number | string | null;
    wheelchair?: number | string | null;
    ride_id?: string;
    user_id?: string;
    userId?: string;
    standard_seat?: number;
    request_time?: string | null;
    request_type?: string;
    requestType?: string;
    from_address?: string;
    to_address?: string;
    originPointId?: string;
    destPointId?: string;
    // Metadata for transit routes pulled from OpenStreetMap (PTv2). Mirrors
    // the existing fixedRouteMetadata pattern so renderers can branch via a
    // type guard without complicating the base Line shape.
    osmRouteMetadata?: import('../types/osmRoutes').OsmRouteMetadata;
}

type Polygon = {
    id: string;
    layerId?: number;
    type: 'polygon';
    name: string;
    colour: string;
    description: string;
    opacity: string;
    borderColour: string;
    borderWidth: number;
    isLocked: boolean;
    coordinates: [number, number][][]
    hidden?: boolean;
    // Population analysis fields
    totalPopulation?: number;
    coveredPopulation?: number;
    desertPopulation?: number;
    percentCovered?: number;
    percentDesert?: number;
    // Timestamp for tracking creation order
    createdAt?: number;
}

type Shape = Point | Line | Polygon | RasterShape

/** Set on a layer when Excel "Squish rides" import combined source rows into fewer trips. */
type TripImportSquishMeta = {
    enabled: true;
    sourceRowCount: number;
    resultTripCount: number;
};

type Layer = {
    layerId: number;
    layerName: string;
    hidden: boolean;
    shapes: Shape[];
    expanded?: boolean; // Optional property to control accordion expansion state
    renderAll?: boolean; // When true, bypasses the trip render cap for this layer
    tripImportSquish?: TripImportSquishMeta;
};

type GlobalContext = {
    mapLayers: Layer[];
    setLayers: (value: Layer[] | ((prev: Layer[]) => Layer[])) => void
    activeLayerId: number | null;
    setActiveLayerId: SetContextNumberFunction;
    idCounter: number;
    setIdCounter: SetContextNumberFunction;
    selectedShapes: string[];
    setSelectedShapes: (value: string[] | ((prev: string[]) => string[])) => void
    fleets: Fleet[];
    setFleets: (value: Fleet[] | ((prev: Fleet[]) => Fleet[])) => void
    services: Service[];
    setServices: (value: Service[] | ((prev: Service[]) => Service[])) => void
};

type LocationSettingsContext = {
    latitude: number,
    longitude: number,
    zoom: number
};

type setMapCentreFunctionType = (value: LocationSettingsContext | ((prev: LocationSettingsContext) => LocationSettingsContext)) => void

type MapSettingsContext = {
    mapName: string;
    setMapName: SetContextStringFunction;
    mapCentre: LocationSettingsContext;
    setMapCentre: setMapCentreFunctionType;
    basemap: string;
    setBasemap: SetContextStringFunction;
};

type MapActionsContext = {
    openSimulationModal: (() => void) | null;
};

type UserDetails = {
    email: string,
    userId: string,
    fullName: string,
    role: 'user' | 'admin',
    /** From admin API / JWT when available; used for customer-scoped feature overrides. */
    customerId?: string | null,
    /** Display name of the user's company. */
    customerName?: string | null,
    /** Company-level role: "company_admin", "member", or null. */
    companyRole?: 'company_admin' | 'member' | null,
};

type setUserDetailsFunctionType = (value: UserDetails | ((prev: UserDetails) => UserDetails)) => void;


type UserData = {
    userDetails: UserDetails;
    setUserDetails: setUserDetailsFunctionType;
};

type ModeSettings = {
    mode: string;
    drawingPolygonPoints: [number, number][];
    drawingLinePoints: [number, number][];
    editingPolygonId: string | null;
    editingVertexIndex: number | null;
    tripOriginPoint?: Point | null;
}

type setModeSettingsFunction = (value: ModeSettings | ((prev: ModeSettings) => ModeSettings)) => void;

type Context = {
    global: GlobalContext,
    mapSettings: MapSettingsContext,
    mapActions?: MapActionsContext,
    userData: UserData,
    modeSettings: ModeSettings,
    setModeSettings: setModeSettingsFunction
};

const AppContext = createContext<Context | undefined>(undefined)

export type {
      Context
    , GlobalContext
    , SetContextStringFunction
    , MapSettingsContext
    , UserDetails
    , UserData
    , LocationSettingsContext
    , SetContextNumberFunction
    , Point
    , Line
    , Polygon
    , Shape
    , Layer
    , TripImportSquishMeta
    , ModeSettings
    , IconType
    , Fleet
    , Service
    , MapActionsContext
}
export {AppContext}

